import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Inbox } from "lucide-react";

export default function ApprovalsInboxPage() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { setRows((await api.get("/certificates/v2/inbox")).data.rows); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    const note = decision === "reject" ? (prompt("Reason for rejection:") || "") : "";
    try {
      await api.post(`/certificates/v2/${id}/approve`, { decision, note });
      toast.success(decision === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <WorkspaceLayout subtitle="HR workspace" title="Approval Inbox">
      {rows.length === 0 ? (
        <Card className="rounded-sm border-ec-line"><CardContent className="p-10 text-center text-ec-ink-soft" data-testid="inbox-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-ec-ink-soft/50" />
          Nothing awaiting your approval right now.
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="inbox-list">
          {rows.map((c) => {
            const step = (c.approval_chain || []).find((s) => !s.decision);
            return (
              <Card key={c.id} className="rounded-sm border-ec-line">
                <CardContent className="p-4 flex flex-wrap gap-3 items-start" data-testid={`inbox-row-${c.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-semibold">{c.title}</div>
                    <div className="text-xs text-ec-ink-soft mt-1">{c.cert_type} · {c.sub_category}</div>
                    <div className="text-[11px] font-mono-plex text-ec-ink-soft mt-1">
                      Recipient: {c.recipient_name} {c.recipient_ethic_id ? `(${c.recipient_ethic_id})` : ""}
                    </div>
                    <div className="text-[11px] font-mono-plex text-ec-ink-soft">Submitted: {c.submitted_at?.slice(0, 16)}</div>
                    {step && <Badge className="rounded-sm mt-2 bg-amber-100 text-amber-900 border-amber-300">Step {step.step}</Badge>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => decide(c.id, "approve")} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-1.5" data-testid={`inbox-approve-${c.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide(c.id, "reject")} className="rounded-sm border-ec-error text-ec-error gap-1.5" data-testid={`inbox-reject-${c.id}`}>
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </WorkspaceLayout>
  );
}
